'use client';

import { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { InView, Stagger, StaggerChild } from '@/components/motion/InView';
import { WordReveal } from '@/components/motion/WordReveal';
import { Link } from '@/i18n/navigation';
import type { PublicMenuCategoryRecord } from '@restaurant/db';
import styles from './TakeawayPageView.module.css';

interface HeroData {
  eyebrow: string;
  title: string;
  description: string;
  summary: {
    eyebrow: string;
    title: string;
    description: string;
  };
  image: {
    src: string;
    alt: string;
  };
}

interface ServiceCard {
  id: string;
  icon: string;
  title: string;
  description: string;
}

interface ScheduleDay {
  key: string;
  label: string;
  hours: string;
}

interface ContactPanelData {
  eyebrow: string;
  title: string;
  description: string;
  addressLabel: string;
  phoneLabel: string;
  hoursLabel: string;
  addressValue: string;
  phoneValue: string;
  hoursValue: string;
  secondaryNote: string;
}

interface StepItem {
  id: string;
  number: string;
  title: string;
  description: string;
}

interface TakeawayPageViewProps {
  data: {
    locale: string;
    actions: {
      contact: string;
      comingSoon: string;
    };
    hero: HeroData;
    service: {
      eyebrow: string;
      title: string;
      description: string;
      cards: readonly ServiceCard[];
    };
    menu: {
      eyebrow: string;
      title: string;
      description: string;
      emptyTitle: string;
      emptyDescription: string;
      updatedLabel: string;
      categories: PublicMenuCategoryRecord[];
    };
    schedule: {
      eyebrow: string;
      title: string;
      description: string;
      badge: string;
      days: readonly ScheduleDay[];
    };
    contactPanel: ContactPanelData;
    steps: {
      eyebrow: string;
      title: string;
      description: string;
      items: readonly StepItem[];
    };
  };
}

const EASE = [0.22, 1, 0.36, 1] as const;

export function TakeawayPageView({ data }: TakeawayPageViewProps) {
  const reduced = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  const [categories, setCategories] = useState(data.menu.categories);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const priceFormatter = useMemo(
    () =>
      new Intl.NumberFormat(data.locale, {
        style: 'currency',
        currency: 'EUR',
      }),
    [data.locale],
  );

  useEffect(() => setMounted(true), []);
  useEffect(() => setCategories(data.menu.categories), [data.menu.categories]);

  useEffect(() => {
    let active = true;

    async function refreshTakeawayMenu() {
      try {
        const response = await fetch(`/api/takeaway-menu?locale=${data.locale}`, {
          cache: 'no-store',
        });
        if (!response.ok) return;

        const payload = (await response.json()) as { categories?: PublicMenuCategoryRecord[] };
        if (!active || !Array.isArray(payload.categories)) return;

        setCategories(payload.categories);
        setLastUpdated(new Date());
      } catch {
        // The server-rendered menu remains available if a background refresh fails.
      }
    }

    const interval = window.setInterval(refreshTakeawayMenu, 5000);
    window.addEventListener('focus', refreshTakeawayMenu);

    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshTakeawayMenu);
    };
  }, [data.locale]);

  return (
    <div className={styles.page}>
      <section className={styles.hero} aria-labelledby="takeaway-hero-title">
        <div className={styles.heroGrid}>
          {mounted && !reduced ? (
            <>
              <div className={styles.heroContent}>
                <motion.span
                  className={styles.eyebrow}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.55, delay: 0.08, ease: EASE }}
                >
                  {data.hero.eyebrow}
                </motion.span>

                <h1 id="takeaway-hero-title" className={styles.heroTitle}>
                  <WordReveal text={data.hero.title} delay={0.16} stagger={0.075} />
                </h1>

                <motion.p
                  className={styles.heroDescription}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.72, delay: 0.4, ease: EASE }}
                >
                  {data.hero.description}
                </motion.p>

                <motion.div
                  className={styles.heroActions}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6, ease: EASE }}
                >
                  <Link href="/contact" className={styles.primaryButton}>
                    {data.actions.contact}
                  </Link>
                  <a href="#takeaway-menu-title" className={styles.secondaryButton}>
                    {data.actions.comingSoon}
                  </a>
                </motion.div>
              </div>

              <div className={styles.heroVisual}>
                <motion.div
                  className={styles.heroImageFrame}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1.05, delay: 0.08, ease: EASE }}
                >
                  <img className={styles.heroImage} src={data.hero.image.src} alt={data.hero.image.alt} />
                </motion.div>

                <motion.article
                  className={styles.summaryCard}
                  initial={{ opacity: 0, x: -26, y: 18 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.52, ease: EASE }}
                >
                  <span className={styles.summaryEyebrow}>{data.hero.summary.eyebrow}</span>
                  <h2 className={styles.summaryTitle}>{data.hero.summary.title}</h2>
                  <p className={styles.summaryDescription}>{data.hero.summary.description}</p>
                </motion.article>
              </div>
            </>
          ) : (
            <>
              <div className={styles.heroContent}>
                <span className={styles.eyebrow}>{data.hero.eyebrow}</span>
                <h1 id="takeaway-hero-title" className={styles.heroTitle}>
                  {data.hero.title}
                </h1>
                <p className={styles.heroDescription}>{data.hero.description}</p>

                <div className={styles.heroActions}>
                  <Link href="/contact" className={styles.primaryButton}>
                    {data.actions.contact}
                  </Link>
                  <a href="#takeaway-menu-title" className={styles.secondaryButton}>
                    {data.actions.comingSoon}
                  </a>
                </div>
              </div>

              <div className={styles.heroVisual}>
                <div className={styles.heroImageFrame}>
                  <img className={styles.heroImage} src={data.hero.image.src} alt={data.hero.image.alt} />
                </div>

                <article className={styles.summaryCard}>
                  <span className={styles.summaryEyebrow}>{data.hero.summary.eyebrow}</span>
                  <h2 className={styles.summaryTitle}>{data.hero.summary.title}</h2>
                  <p className={styles.summaryDescription}>{data.hero.summary.description}</p>
                </article>
              </div>
            </>
          )}
        </div>
      </section>

      <section className={styles.serviceSection} aria-labelledby="takeaway-service-title">
        <InView className={styles.sectionIntro} y={28} delay={0.02}>
          <span className={styles.eyebrow}>{data.service.eyebrow}</span>
          <h2 id="takeaway-service-title" className={styles.sectionTitle}>
            {data.service.title}
          </h2>
          <p className={styles.sectionDescription}>{data.service.description}</p>
        </InView>

        <Stagger className={styles.cardGrid} stagger={0.1} delayChildren={0.06}>
          {data.service.cards.map((card) => (
            <StaggerChild key={card.id} y={28} duration={0.68}>
              <article className={styles.serviceCard}>
                <span className={`material-symbols-outlined ${styles.serviceIcon}`} aria-hidden="true">
                  {card.icon}
                </span>
                <h3 className={styles.cardTitle}>{card.title}</h3>
                <p className={styles.cardDescription}>{card.description}</p>
              </article>
            </StaggerChild>
          ))}
        </Stagger>
      </section>

      <section className={styles.menuSection} aria-labelledby="takeaway-menu-title">
        <InView className={styles.sectionIntro} y={28} delay={0.02}>
          <span className={styles.eyebrow}>{data.menu.eyebrow}</span>
          <h2 id="takeaway-menu-title" className={styles.sectionTitle}>
            {data.menu.title}
          </h2>
          <p className={styles.sectionDescription}>{data.menu.description}</p>
          {lastUpdated && (
            <p className={styles.liveNote}>
              {data.menu.updatedLabel} {lastUpdated.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </InView>

        {categories.length > 0 ? (
          <div className={styles.takeawayCatalog}>
            {categories.map((category) => (
              <InView key={category.id} y={22} duration={0.65}>
                <article className={styles.categoryPanel}>
                  <header className={styles.categoryHeader}>
                    <div>
                      <span className={styles.categoryKicker}>{category.items.length} plats</span>
                      <h3>{category.name}</h3>
                    </div>
                    {category.description && <p>{category.description}</p>}
                  </header>

                  <div className={styles.dishList}>
                    {category.items.map((item) => (
                      <div key={item.id} className={styles.dishRow}>
                        <div className={styles.dishContent}>
                          <div className={styles.dishTitleRow}>
                            <h4>{item.name}</h4>
                            <span>{priceFormatter.format(item.price)}</span>
                          </div>
                          <p>{item.description || item.longDescription}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              </InView>
            ))}
          </div>
        ) : (
          <InView y={22} duration={0.65}>
            <article className={styles.emptyMenu}>
              <h3>{data.menu.emptyTitle}</h3>
              <p>{data.menu.emptyDescription}</p>
            </article>
          </InView>
        )}
      </section>

      <section className={styles.detailSection} aria-labelledby="takeaway-schedule-title">
        <InView x={-26} y={0} duration={0.8}>
          <article className={styles.scheduleCard}>
            <span className={styles.eyebrow}>{data.schedule.eyebrow}</span>
            <h2 id="takeaway-schedule-title" className={styles.sectionTitle}>
              {data.schedule.title}
            </h2>
            <p className={styles.sectionDescription}>{data.schedule.description}</p>
            <span className={styles.scheduleBadge}>{data.schedule.badge}</span>

            <div className={styles.scheduleList}>
              {data.schedule.days.map((day) => (
                <div key={day.key} className={styles.scheduleRow}>
                  <span className={styles.dayLabel}>{day.label}</span>
                  <span className={styles.dayHours}>{day.hours}</span>
                </div>
              ))}
            </div>
          </article>
        </InView>

        <InView x={26} y={0} delay={0.08} duration={0.8}>
          <aside className={styles.contactCard}>
            <span className={styles.eyebrow}>{data.contactPanel.eyebrow}</span>
            <h2 className={styles.sectionTitle}>{data.contactPanel.title}</h2>
            <p className={styles.sectionDescription}>{data.contactPanel.description}</p>

            <dl className={styles.contactDetails}>
              <div className={styles.detailItem}>
                <dt>{data.contactPanel.addressLabel}</dt>
                <dd>{data.contactPanel.addressValue}</dd>
              </div>
              <div className={styles.detailItem}>
                <dt>{data.contactPanel.phoneLabel}</dt>
                <dd>{data.contactPanel.phoneValue}</dd>
              </div>
              <div className={styles.detailItem}>
                <dt>{data.contactPanel.hoursLabel}</dt>
                <dd>{data.contactPanel.hoursValue}</dd>
              </div>
            </dl>

            <Link href="/contact" className={styles.contactButton}>
              {data.actions.contact}
            </Link>
            <p className={styles.secondaryNote}>{data.contactPanel.secondaryNote}</p>
          </aside>
        </InView>
      </section>

      <section className={styles.stepsSection} aria-labelledby="takeaway-steps-title">
        <InView className={styles.sectionIntroCentered} y={28}>
          <span className={styles.eyebrow}>{data.steps.eyebrow}</span>
          <h2 id="takeaway-steps-title" className={styles.sectionTitle}>
            {data.steps.title}
          </h2>
          <p className={styles.sectionDescription}>{data.steps.description}</p>
        </InView>

        <Stagger className={styles.stepsGrid} stagger={0.11} delayChildren={0.05}>
          {data.steps.items.map((step) => (
            <StaggerChild key={step.id} y={26} duration={0.7}>
              <article className={styles.stepCard}>
                <span className={styles.stepNumber}>{step.number}</span>
                <h3 className={styles.cardTitle}>{step.title}</h3>
                <p className={styles.cardDescription}>{step.description}</p>
              </article>
            </StaggerChild>
          ))}
        </Stagger>
      </section>
    </div>
  );
}
